// Multi-line rent (one TVA rate per line). Shared by edge functions and the frontend.
// A lease with `rent_lines` (2+ entries) uses them; otherwise the legacy
// rent_amount / charges_amount / vat_rate fields apply unchanged.

export interface LeaseLine {
  label: string;
  rent_ht: number;
  charges_ht: number;
  vat_rate: number;
}

export function getLeaseLines(lease: any): LeaseLine[] | null {
  const raw = lease?.rent_lines;
  if (!Array.isArray(raw) || raw.length === 0) return null;
  return raw.map((l: any) => ({
    label: String(l?.label || '').trim(),
    rent_ht: Number(l?.rent_ht) || 0,
    charges_ht: Number(l?.charges_ht) || 0,
    vat_rate: Number(l?.vat_rate) || 0,
  }));
}

const round2 = (n: number) => Math.round(n * 100) / 100;
const cents = (n: number) => Math.round(n * 100);

export type InvoiceItem = { label: string; annotation?: string | null; type: string; amountHT: number; vatRate: number; amountTTC: number };

/** Round VAT once per rate, then assign any remaining cent to the lines closest to rounding up. */
export function allocateVatByRate<T extends InvoiceItem>(items: T[]): T[] {
  const allocated = items.map(item => ({
    ...item,
    amountHT: cents(item.amountHT) / 100,
    amountTTC: cents(item.amountHT) / 100,
  })) as T[];
  const rates = new Map<number, number[]>();
  allocated.forEach((item, index) => {
    if (item.vatRate <= 0) return;
    rates.set(item.vatRate, [...(rates.get(item.vatRate) ?? []), index]);
  });

  for (const [rate, indices] of rates) {
    const shares = indices.map(index => {
      const exact = cents(allocated[index].amountHT) * rate / 100;
      const rounded = Math.round(exact);
      allocated[index].amountTTC = (cents(allocated[index].amountHT) + rounded) / 100;
      return { index, rounded, remainder: exact - rounded };
    });
    const taxableCents = indices.reduce((sum, index) => sum + cents(allocated[index].amountHT), 0);
    const vatCents = Math.round(taxableCents * rate / 100);
    const difference = vatCents - shares.reduce((sum, share) => sum + share.rounded, 0);
    shares.sort((a, b) => (difference > 0 ? b.remainder - a.remainder : a.remainder - b.remainder) || a.index - b.index);
    for (let cent = 0; cent < Math.abs(difference); cent++) {
      const item = allocated[shares[cent % shares.length].index];
      item.amountTTC = (cents(item.amountTTC) + Math.sign(difference)) / 100;
    }
  }
  return allocated;
}

// The line's TVA applies to both its loyer and its charges.
export function leaseLineTotals(lines: LeaseLine[]) {
  const items = leaseLinesToInvoiceItems(lines);
  const sum = (type: string, field: 'amountHT' | 'amountTTC') =>
    items.filter(item => item.type === type).reduce((total, item) => total + cents(item[field]), 0) / 100;
  const rentHt = sum('rent', 'amountHT');
  const chargesHt = sum('charges', 'amountHT');
  const rentTtc = sum('rent', 'amountTTC');
  const chargesTtc = sum('charges', 'amountTTC');
  const ht = rentHt + chargesHt;
  const ttc = rentTtc + chargesTtc;
  return {
    rentHt: round2(rentHt),
    chargesHt: round2(chargesHt),
    rentTtc: round2(rentTtc),
    chargesTtc: round2(chargesTtc),
    ht: round2(ht),
    vat: round2(ttc - ht),
    ttc: round2(ttc),
  };
}

export function leaseLinesToInvoiceItems(lines: LeaseLine[]) {
  const items: InvoiceItem[] = [];
  lines.forEach((l, i) => {
    const name = l.label || `Ligne ${i + 1}`;
    if (l.rent_ht) items.push({ label: `Loyer — ${name}`, type: 'rent', amountHT: l.rent_ht, vatRate: l.vat_rate, amountTTC: l.rent_ht });
    if (l.charges_ht) items.push({ label: `Charges — ${name}`, type: 'charges', amountHT: l.charges_ht, vatRate: l.vat_rate, amountTTC: l.charges_ht });
  });
  return allocateVatByRate(items);
}

// Lines for a billed period: the period's own lines (e.g. annual indexation) win over the lease lines.
export function periodInvoiceItems(lease: any, rentPeriod: any): InvoiceItem[] | null {
  const own = rentPeriod?.invoice_lines;
  if (Array.isArray(own) && own.length > 0) {
    return own.map((l: any) => ({
      label: String(l?.label || ''),
      annotation: l?.annotation ?? null,
      type: String(l?.type || 'rent'),
      amountHT: Number(l?.amountHT) || 0,
      vatRate: Number(l?.vatRate) || 0,
      amountTTC: Number(l?.amountTTC) || 0,
    }));
  }
  const lines = getLeaseLines(lease);
  return lines ? leaseLinesToInvoiceItems(lines) : null;
}
